/* DateTimePickerComponent.js */

"use strict";
const _ = require("lodash");
const InputBaseControl = require("@gluenetworks/input_base_control").Base;
const Renderer = require("./DateTimePickerRenderer");
const moment = require("moment");

class DateTimePickerComponent extends InputBaseControl {
    /**
     * @schema.name Date Time Picker Control
     * @schema.package @gluenetworks/date_time_picker
     * @schema.packageIdentifier DateTimePicker
     * @schema.namespace require("@gluenetworks/steve_ui_core").Control.Timestamp.DateTimePicker
     * @schema.events ["Disabled", "Value", "Visible", "DarkTheme", "Theme", "Focus", "Blur", "SetAttribute", "UserValue", "UserFocus", "UserBlur"]
     *
     * @schema.configuration.showTime.name showTime
     * @schema.configuration.showTime.type boolean
     * @schema.configuration.showTime.default true
     * @schema.configuration.showTime.required false
     *
     * @schema.configuration.showDate.name showDate
     * @schema.configuration.showDate.type boolean
     * @schema.configuration.showDate.default true
     * @schema.configuration.showDate.required false
     *
     * @schema.configuration.view.name view
     * @schema.configuration.view.type string
     * @schema.configuration.view.default days
     * @schema.configuration.view.required false
     *
     * @schema.configuration.value.example { year: 1999, month: 1, date:1, hour: 1, minute:1 }
     */

    constructor(config) {
        super(config);
        this._showTime = _.get(config, "showTime", true);
        this._showDate = _.get(config, "showDate", true);
        if (this._showTime && this._showDate) {
            this._views = ["days", "years", "hours", "minutes"];
            this._view = "days";
        } else if (this._showDate) {
            this._views = ["days", "years"];
            this._view = "days";
        } else if (this._showTime) {
            this._views = ["hours", "minutes"];
            this._view = "hours";
        } else {
            this._views = [];
        }
        this.setValue(config.value);
        this._createRenderer();
        this.setView(this._view);
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/

    _createRenderer() {
        this._renderer = new Renderer(this);
    }

    _toggleMeridiem() {
        if (this.disabled) {
            return this;
        }
        let h = this._value.get("hour");
        if (h > 12) {
            h = h - 12;
        } else {
            h = h + 12;
        }
        this.setValue({hour: h});
        return this;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    /**
     * @schema.methods.disable.name disable
     * @schema.methods.disable.description sets disabled true
     * @schema.methods.disable.returns this
     */

    disable() {
        this.setDisabled(true);
        return this;
    }

    /**
     * @schema.methods.enable.name enable
     * @schema.methods.enable.description sets disabled false
     * @schema.methods.enable.returns this
     */

    enable() {
        this.setDisabled(false);
        return this;
    }

    get showTime() {
        return this.getShowTime();
    }

    set showTime(bool) {
        this.setShowTime(bool);
    }

    /**
     * @schema.methods.getShowTime.name getShowTime
     * @schema.methods.getShowTime.description Is time being displayed
     * @schema.methods.getShowTime.returns boolean
     */

    getShowTime() {
        return this._showTime;
    }

    /**
     * @schema.methods.setShowTime.name setShowTime
     * @schema.methods.setShowTime.description display time or not
     * @schema.methods.setShowTime.returns this
     * @schema.methods.setShowTime.parameters.bool.name bool
     * @schema.methods.setShowTime.parameters.bool.description true to display time
     * @schema.methods.setShowTime.parameters.bool.type boolean
     */

    setShowTime(bool = true) {
        if (this.disabled) {
            return this;
        }
        let oldVal = this._showTime;
        this._showTime = bool;
        this.emit("ShowTime", oldVal, this._showTime);
        return this;
    }

    toggleShowTime() {
        this.setShowTime(!this._showTime);
    }

    get showDate() {
        return this.getShowDate();
    }

    set showDate(bool) {
        this.setShowDate(bool);
    }

    /**
     * @schema.methods.getShowDate.name getShowDate
     * @schema.methods.getShowDate.description Is date being displayed
     * @schema.methods.getShowDate.returns boolean
     */

    getShowDate() {
        return this._showDate;
    }

    /**
     * @schema.methods.setShowDate.name setShowDate
     * @schema.methods.setShowDate.description display date or not
     * @schema.methods.setShowDate.returns this
     * @schema.methods.setShowDate.parameters.bool.name bool
     * @schema.methods.setShowDate.parameters.bool.description true to display date
     * @schema.methods.setShowDate.parameters.bool.type boolean
     */

    setShowDate(bool = true) {
        if (this.disabled) {
            return this;
        }
        let oldVal = this._showDate;
        this._showDate = bool;
        this.emit("ShowDate", oldVal, this._showDate);
        return this;
    }

    toggleShowDate() {
        this.setShowDate(!this._showDate);
    }

    get view() {
        return this.getView();
    }

    set view(v) {
        this.setView(v);
    }

    /**
     * @schema.methods.getView.name getView
     * @schema.methods.getView.description get what view is currently being displayed. (days,years,hours,minutes)
     * @schema.methods.getView.returns string
     */
    getView() {
        return this._view;
    }

    /**
     * @schema.methods.setView.name setView
     * @schema.methods.setView.description set the view to one of days, years, hours, or minutes
     * @schema.methods.setView.returns this
     * @schema.methods.setView.parameters.viewName.name viewName
     * @schema.methods.setView.parameters.viewName.description name of the view
     * @schema.methods.setView.parameters.viewName.type string
     */
    setView(viewName) {
        if (this.disabled) {
            return this;
        }
        if (this._views.includes(viewName)) {
            this._view = viewName;
        } else {
            this._view = "days";
        }
        this.emit("View", this._view);
        return this;
    }

    showHours() {
        if (this.disabled) {
            return this;
        }
        this.setView("hours");
        this.emit("ShowHours");
        return this;
    }

    showMinutes() {
        if (this.disabled) {
            return this;
        }
        this.setView("minutes");
        this.emit("ShowMinutes");
        return this;
    }

    showDays() {
        if (this.disabled) {
            return this;
        }
        this.setView("days");
        this.emit("ShowDays");
        return this;
    }

    showYears() {
        if (this.disabled) {
            return this;
        }
        this.setView("years");
        this.emit("ShowYears");
        return this;
    }

    /**
     * @schema.methods.setValue.name setValue
     * @schema.methods.setValue.description Set the date and time value
     * @schema.methods.setValue.returns this
     * @schema.methods.setValue.parameters.newVal.name newVal
     * @schema.methods.setValue.parameters.newVal.description an instance of moment or a date object {year:2017,date:29,hour:20,minute:30}
     * @schema.methods.setValue.parameters.newVal.type object|moment
     */
    setValue(newVal, wasTriggeredByUser) {
        if (this.disabled) {
            return this;
        }
        newVal = newVal || moment();
        if (moment(newVal).isValid()) {
            let prev = this._value;
            if (!(this._value instanceof moment)) {
                this._value = moment();
            }
            if (newVal instanceof moment) {
                this._value = newVal;
            } else {
                this._value.set(newVal);
            }
            this.emit("Value", this._value, prev, wasTriggeredByUser);
            if (wasTriggeredByUser) {
                this._triggerUserValue(this._value, prev, event);
            }
        }
        return this;
    }

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        let prom = this._renderer.shutdown();
        this._renderer = undefined;
        return prom;
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/
    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = DateTimePickerComponent;
